// eslint-disable-next-line no-restricted-imports
import { Percent, TradeType } from '@kodiak-finance/sdk-core'
import { simulateSwapWithV1 } from 'components/swap/simulateSwap'
import { useConnection } from 'hooks/useConnection'
import { SwapCallbackState, useSwapCallback as useLibSwapCallBack } from 'lib/hooks/swap/useSwapCallback'
import { ReactNode, useMemo } from 'react'
import { Trade } from 'state/routing/trades'

import { useTransactionAdder } from '../state/transactions/hooks'
import { TransactionType } from '../state/transactions/types'
import { currencyId } from '../utils/currencyId'
import { useSwapCallArguments } from './useSwapCallArguments'

// returns a function that will execute a swap, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function useSwapCallback(
  trade: Trade | undefined,
  allowedSlippage: Percent, // in bips
  recipientAddress: string | null // the address of the recipient of the trade, or null if swap should be returned to sender
): { state: SwapCallbackState; callback: null | (() => Promise<string>); error: ReactNode | null } {
  const { account, wagmiPublicClient, chainId } = useConnection()

  const addTransaction = useTransactionAdder()

  const recipient = recipientAddress === null ? account : recipientAddress

  const {
    state,
    callback: libCallback,
    error,
  } = useLibSwapCallBack({
    trade,
    recipientAddress: recipient,
  })

  const swapCalls = useSwapCallArguments(trade, recipient)

  const callback = useMemo(() => {
    if (!libCallback || !trade || !wagmiPublicClient || !account || !recipient) {
      return null
    }
    return async () => {
      try {
        const minAmountOut = trade.minimumAmountOut(allowedSlippage)

        // Get the swap call parameters
        if (swapCalls.length > 0 && chainId) {
          await simulateSwapWithV1(
            wagmiPublicClient,
            swapCalls[0],
            account,
            recipient,
            trade.inputAmount.currency,
            trade.outputAmount.currency,
            minAmountOut,
            chainId
          )
        }
      } catch (simulationError: any) {
        console.error('eth_simulateV1 error:', simulationError)
        throw new Error(`Transaction simulation failed: ${simulationError.message}`)
      }

      const response = await libCallback()

      addTransaction(
        { hash: response.hash } as any,
        trade.tradeType === TradeType.EXACT_INPUT
          ? {
              type: TransactionType.SWAP,
              tradeType: TradeType.EXACT_INPUT,
              inputCurrencyId: currencyId(trade.inputAmount.currency),
              inputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
              expectedOutputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
              outputCurrencyId: currencyId(trade.outputAmount.currency),
              minimumOutputCurrencyAmountRaw: trade.minimumAmountOut(allowedSlippage).quotient.toString(),
              recipient: recipient || account,
              isCustomRecipient: recipient ? recipient !== account : undefined,
            }
          : {
              type: TransactionType.SWAP,
              tradeType: TradeType.EXACT_OUTPUT,
              inputCurrencyId: currencyId(trade.inputAmount.currency),
              maximumInputCurrencyAmountRaw: trade.maximumAmountIn(allowedSlippage).quotient.toString(),
              outputCurrencyId: currencyId(trade.outputAmount.currency),
              outputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
              expectedInputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
              recipient: recipient || account,
              isCustomRecipient: recipient ? recipient !== account : undefined,
            }
      )
      return response.hash
    }
  }, [libCallback, trade, wagmiPublicClient, account, recipient, addTransaction, allowedSlippage, swapCalls, chainId])

  return {
    state,
    callback: callback as (() => Promise<string>) | null,
    error,
  }
}
